import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ChevronDown, Trash2, Check, Loader2, Shield, Flame, TrendingUp, Code, Users, Sparkles, Rocket, Globe, X, Zap, Target, BarChart3, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const safeStr = (val) => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  return JSON.stringify(val);
};

const THINKING_LINES = [
  "Analyzing trust signals in positioning...",
  "This doesn't look like a mass-market play...",
  "High-value audience indicators present...",
  "Checking private vs public distribution fit...",
  "Authority channels likely outperform viral...",
  "Evaluating product-market alignment depth...",
  "Your audience avoids mass channels...",
  "Assessing competitive moat strength...",
];

const INSIGHT_STEPS = [
  { key: 'scraping', label: 'Analyzing positioning', insight: 'Product positioning extracted' },
  { key: 'scoring', label: 'Mapping audience & channels', insight: 'Audience pattern detected' },
  { key: 'fast_ready', label: 'Evaluating growth strategies', insight: null },
  { key: 'deep_analyzing', label: 'Preparing execution plan', insight: null },
];

const MODE_ICONS = { viral: Flame, authority: Shield, conversion: TrendingUp, technical: Code, community: Users };
const MODE_COLORS = { viral: '#EF4444', authority: '#8B5CF6', conversion: '#10B981', technical: '#3B82F6', community: '#F59E0B' };

// Fallback signals if API hasn't loaded yet
const FALLBACK_SIGNALS = [
  { text: 'Analyzing market patterns...', delta: '', color: '#7C5CFF' },
];

export default function HomePage() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [showOptions, setShowOptions] = useState(false);
  const [options, setOptions] = useState({ goal: '', geography: '', audience: '', tone: '' });
  const [progressStatus, setProgressStatus] = useState('pending');
  const [fastResult, setFastResult] = useState(null);
  const [analyzeUrl, setAnalyzeUrl] = useState('');
  const [thinkIdx, setThinkIdx] = useState(0);
  const [revealedModes, setRevealedModes] = useState([]);
  const [activated, setActivated] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [signalIdx, setSignalIdx] = useState(0);
  const [previewModal, setPreviewModal] = useState(false);
  const [liveCount, setLiveCount] = useState({ strategies: 0, deployed: 0 });
  const [liveSignals, setLiveSignals] = useState([]);
  const [topEdge, setTopEdge] = useState(null);
  const navigate = useNavigate();

  const loadProjects = useCallback(async () => {
    try { const res = await axios.get(`${API}/projects`); setProjects(res.data); } catch {}
  }, []);

  // Fetch real market signals
  const loadSignals = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/market-signals`);
      const data = res.data;
      // Map signals to display format
      if (data.signals?.length) {
        setLiveSignals(data.signals.map(s => ({
          text: `${s.category}: ${s.insight}`,
          delta: s.delta || '',
          color: s.delta_direction === 'up' ? '#22C55E' : '#EF4444'
        })));
      }
      if (data.top_edge) setTopEdge(data.top_edge);
      if (data.stats) {
        setLiveCount({
          strategies: data.stats.total_analyzed || 0,
          deployed: data.stats.total_deployed || 0
        });
      }
    } catch {}
  }, []);

  useEffect(() => { loadProjects(); loadSignals(); }, [loadProjects, loadSignals]);

  // Force dark body
  useEffect(() => {
    document.body.style.backgroundColor = '#07070A';
    document.body.style.color = '#fff';
    return () => { document.body.style.backgroundColor = ''; document.body.style.color = ''; };
  }, []);

  // Rotate live signals
  const activeSignals = liveSignals.length > 0 ? liveSignals : FALLBACK_SIGNALS;
  useEffect(() => {
    const iv = setInterval(() => setSignalIdx(p => (p + 1) % Math.max(activeSignals.length, 1)), 3000);
    return () => clearInterval(iv);
  }, [activeSignals.length]);

  // Gentle animation on real metrics (only cosmetic drift, not fake inflation)
  useEffect(() => {
    if (liveCount.strategies === 0) return;
    const iv = setInterval(() => {
      setLiveCount(p => ({
        strategies: p.strategies + (Math.random() > 0.7 ? 1 : 0),
        deployed: p.deployed + (Math.random() > 0.85 ? 1 : 0)
      }));
    }, 8000);
    return () => clearInterval(iv);
  }, [liveCount.strategies]);

  useEffect(() => {
    if (!loading || fastResult) return;
    const iv = setInterval(() => setThinkIdx(p => (p + 1) % THINKING_LINES.length), 2800);
    return () => clearInterval(iv);
  }, [loading, fastResult]);

  useEffect(() => {
    if (!fastResult || revealedModes.length > 0) return;
    const modes = Object.keys(fastResult.mode_scores || {});
    modes.forEach((mode, i) => { setTimeout(() => setRevealedModes(p => [...p, mode]), (i + 1) * 400); });
    setTimeout(() => setActivated(true), (modes.length + 1) * 400 + 600);
  }, [fastResult, revealedModes.length]);

  const pollForCompletion = async (projectId) => {
    for (let i = 0; i < 120; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await axios.get(`${API}/projects/${projectId}/status`);
        const { status, fast_result } = res.data;
        setProgressStatus(status);
        if (fast_result && !fastResult) setFastResult(fast_result);
        if ((status === 'fast_ready' || status === 'deep_analyzing' || status === 'complete') && fast_result) {
          const modeCount = Object.keys(fast_result.mode_scores || {}).length;
          const revealTime = (modeCount + 1) * 400 + 600 + 1500;
          await new Promise(r => setTimeout(r, revealTime));
          setTransitioning(true);
          await new Promise(r => setTimeout(r, 600));
          navigate(`/dashboard/${projectId}`);
          return;
        }
        if (status === 'complete' && !fast_result) { navigate(`/dashboard/${projectId}`); return; }
        if (status === 'error') { setLoading(false); return; }
      } catch {}
    }
    setLoading(false);
  };

  const handleAnalyze = async () => {
    if (!url.trim()) return;
    setLoading(true); setProgressStatus('pending'); setFastResult(null); setAnalyzeUrl(url.trim());
    setRevealedModes([]); setActivated(false); setTransitioning(false);
    try {
      const res = await axios.post(`${API}/analyze`, {
        project_url: url.trim(), extra_notes: null,
        optional_goal: options.goal || null, optional_target_geography: options.geography || null,
        optional_target_audience: options.audience || null, optional_brand_tone_preference: options.tone || null
      });
      pollForCompletion(res.data.id);
    } catch { setLoading(false); }
  };

  const handleDelete = async (e, projectId) => {
    e.stopPropagation();
    try { await axios.delete(`${API}/projects/${projectId}`); setProjects(prev => prev.filter(p => p.id !== projectId)); } catch {}
  };

  const getStepStatus = (stepKey) => {
    const order = ['pending', 'scraping', 'scoring', 'fast_ready', 'deep_analyzing', 'complete'];
    const ci = order.indexOf(progressStatus);
    const si = order.indexOf(stepKey);
    if (si < ci) return 'done'; if (si === ci) return 'active'; return 'waiting';
  };

  // ═══ LOADING SCREEN — Single unified card ═══
  if (loading) {
    const domain = analyzeUrl.replace(/https?:\/\//, '').split('/')[0];
    const bestMode = fastResult?.best_mode || '';
    const bestColor = MODE_COLORS[bestMode] || '#8B5CF6';
    const BestIcon = MODE_ICONS[bestMode] || Shield;

    return (
      <div className={`h-screen bg-[#07070A] flex items-center justify-center px-4 transition-opacity duration-500 ${transitioning ? 'opacity-0' : 'opacity-100'}`} data-testid="loading-screen">
        <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: '#0E0E14', border: '1px solid rgba(124,92,255,0.12)', boxShadow: '0 0 60px rgba(124,92,255,0.06)' }}>
          {/* Glow accent */}
          <div className="h-[2px]" style={{ background: 'linear-gradient(90deg, #7C5CFF, #FF8A3D, #7C5CFF)', opacity: 0.6 }} />

          {/* Header */}
          <div className="text-center px-6 pt-5 pb-3">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#7C5CFF] mb-1">NextMove</p>
            <h2 className="text-base font-semibold text-white">Analyzing <span className="text-zinc-400">{domain}</span></h2>
          </div>

          {/* Progress Steps — always visible */}
          <div className="px-6 pb-4 space-y-2.5">
            {INSIGHT_STEPS.map(({ key, label, insight }) => {
              const st = getStepStatus(key);
              return (
                <div key={key} className={`transition-all duration-500 ${st === 'waiting' ? 'opacity-20' : 'opacity-100'}`} data-testid={`progress-${key}`}>
                  <div className="flex items-center gap-2.5">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${st === 'done' ? 'bg-[#22C55E]' : st === 'active' ? 'bg-[#3B82F6]' : 'bg-[#15151D]'}`}>
                      {st === 'done' ? <Check className="w-2.5 h-2.5 text-white" /> : st === 'active' ? <Loader2 className="w-2.5 h-2.5 text-white animate-spin" /> : <div className="w-1 h-1 rounded-full bg-zinc-600" />}
                    </div>
                    <span className={`text-[12px] ${st === 'done' ? 'text-zinc-300' : st === 'active' ? 'text-white font-medium' : 'text-zinc-600'}`}>{label}</span>
                    {st === 'done' && insight && <span className="text-[10px] text-zinc-600 font-mono ml-auto">"{insight}"</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Thinking line */}
          {!fastResult && (
            <div className="px-6 pb-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
              <p className="text-[11px] text-zinc-500 italic text-center pt-3 transition-all duration-300" key={thinkIdx}>{THINKING_LINES[thinkIdx]}</p>
            </div>
          )}

          {/* Mode Scores — slides in when fastResult arrives */}
          {fastResult && (
            <div className="px-6 pb-4 border-t anim-fade-up" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
              <p className="text-[9px] font-mono uppercase tracking-wider text-zinc-500 pt-3 mb-2">Strategy Modes</p>
              <div className="space-y-1.5">
                {Object.entries(fastResult.mode_scores || {}).map(([mode, score]) => {
                  const revealed = revealedModes.includes(mode); const isBest = mode === bestMode;
                  const ModeIcon = MODE_ICONS[mode] || Shield; const color = MODE_COLORS[mode] || '#8B5CF6';
                  return (
                    <div key={mode} className={`flex items-center gap-2 transition-all duration-500 ${revealed ? 'opacity-100' : 'opacity-0'}`}>
                      <ModeIcon className="w-3 h-3 flex-shrink-0" style={{ color: revealed ? (isBest ? color : '#52525B') : '#15151D' }} />
                      <span className={`text-[11px] font-mono w-20 ${isBest ? 'text-white font-bold' : 'text-zinc-500'}`}>{mode}</span>
                      <div className="flex-1 h-1.5 bg-[#15151D] rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${revealed ? 'duration-700' : 'duration-0'}`} style={{ width: revealed ? `${score}%` : '0%', backgroundColor: isBest ? color : '#3F3F46' }} />
                      </div>
                      <span className={`font-mono text-[11px] w-6 text-right ${isBest ? 'font-bold' : ''}`} style={{ color: isBest ? color : '#52525B' }}>{revealed ? score : ''}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Activation — appears at the bottom when ready */}
          {activated && !transitioning && (
            <div className="px-6 pb-5 border-t text-center anim-scale-in" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
              <div className="flex items-center justify-center gap-2 pt-3 mb-1">
                <BestIcon className="w-5 h-5" style={{ color: bestColor }} />
                <span className="text-sm font-bold text-white uppercase">{bestMode} Strategy Activated</span>
              </div>
              <p className="text-[11px] text-zinc-400">{fastResult.headline}</p>
              <p className="text-[10px] text-[#7C5CFF] animate-pulse mt-2">Entering command center...</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══ PREVIEW DATA ═══
  const latest = projects[0];
  const la = latest?.analysis || {};
  const lf = latest?.fast_result || {};
  const previewModes = lf.mode_scores || { viral: 42, authority: 91, conversion: 76, technical: 58 };
  const previewBest = lf.best_mode || 'authority';
  const previewBlocks = [
    { label: 'Audience', value: la.best_marketing_opportunity_today?.target_audience || 'Growth Founders' },
    { label: 'Channel', value: la.best_marketing_opportunity_today?.recommended_channel || 'LinkedIn' },
    { label: 'Hook', value: la.best_marketing_opportunity_today?.recommended_hook || 'Authority-first' },
    { label: 'Format', value: la.best_marketing_opportunity_today?.recommended_format || 'Case Study' },
  ];
  const rawPS = la.best_marketing_opportunity_today?.confidence_score || lf.confidence || 91;
  const previewScore = rawPS > 0 && rawPS <= 10 ? rawPS * 10 : rawPS;
  const previewInsight = lf.headline || la.best_marketing_opportunity_today?.title || 'Authority strategies outperform viral for B2B products';
  const previewSummary = la.best_marketing_opportunity_today?.opportunity_summary || '';
  const signal = activeSignals[signalIdx % activeSignals.length] || FALLBACK_SIGNALS[0];

  // ═══ MAIN LANDING ═══
  return (
    <div className="min-h-screen font-landing relative" style={{ background: '#07070A' }} data-testid="homepage">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="bg-grid-animated absolute inset-0" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[800px] rounded-full" style={{ background: 'radial-gradient(ellipse, rgba(124,92,255,0.08) 0%, rgba(59,130,246,0.03) 40%, transparent 70%)', animation: 'glowPurple 6s ease-in-out infinite' }} />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full" style={{ background: 'radial-gradient(ellipse, rgba(255,138,61,0.04) 0%, transparent 60%)' }} />
      </div>

      {/* ═══ HERO ═══ */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 pt-20 pb-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] px-4 py-1.5 rounded-full mb-6 anim-fade-up" style={{ color: '#7C5CFF', background: 'rgba(124,92,255,0.06)', border: '1px solid rgba(124,92,255,0.12)', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600 }}>
            <Sparkles className="w-3 h-3" />NextMove
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] mb-4 text-gradient-purple anim-fade-up stagger-1" data-testid="hero-title" style={{ fontFamily: "'Inter', sans-serif" }}>
            Your growth edge.<br />Updated daily.
          </h1>

          <p className="text-base max-w-2xl mx-auto anim-fade-up stagger-2" style={{ color: '#71717A', lineHeight: 1.6 }}>
            We analyze global market signals to give you the exact move to execute today.
          </p>
        </div>

        {/* Command Input */}
        <div className="max-w-2xl mx-auto anim-fade-up stagger-3">
          <div className="glass-input rounded-2xl p-2 flex flex-col sm:flex-row gap-2">
            <div className="flex items-center gap-2 flex-1">
              <div className="flex items-center pl-2 text-zinc-600"><Globe className="w-5 h-5" /></div>
              <input data-testid="url-input" type="url" placeholder="Paste your product URL"
                value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAnalyze()}
                className="flex-1 bg-transparent border-0 outline-none text-base h-12 text-white placeholder:text-zinc-600 min-w-0" style={{ fontFamily: "'Inter', sans-serif" }} />
            </div>
            <button data-testid="analyze-btn" onClick={handleAnalyze} disabled={!url.trim()}
              className="h-12 px-6 sm:px-8 rounded-xl text-white text-sm font-semibold btn-glow disabled:opacity-30 flex items-center justify-center gap-2 flex-shrink-0 w-full sm:w-auto"
              style={{ background: 'linear-gradient(135deg, #7C5CFF, #FF8A3D)', boxShadow: '0 4px 20px rgba(124,92,255,0.3), 0 2px 10px rgba(255,138,61,0.2)' }}>
              Find My Edge <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Examples row */}
          <div className="flex flex-wrap items-center justify-between gap-2 mt-3 px-1">
            <p className="text-[11px] text-zinc-600 font-mono hidden sm:block">Daily strategy. Real execution.</p>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] text-zinc-600">Try:</span>
              {['stripe.com', 'linear.app', 'notion.so'].map(ex => (
                <button key={ex} onClick={() => { setUrl(`https://${ex}`); setTimeout(() => { document.querySelector('[data-testid="analyze-btn"]')?.click(); }, 100); }} data-testid={`example-${ex}`}
                  className="text-[10px] font-mono px-2.5 py-1 rounded-lg transition-all hover:-translate-y-0.5 hover:shadow-[0_0_12px_rgba(124,92,255,0.2)]"
                  style={{ color: '#7C5CFF', background: 'rgba(124,92,255,0.06)', border: '1px solid rgba(124,92,255,0.1)' }}>
                  {ex}
                </button>
              ))}
            </div>
          </div>

          {/* Advanced options */}
          <button data-testid="advanced-options-toggle" onClick={() => setShowOptions(!showOptions)}
            className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider mt-3 ml-1 text-zinc-600 hover:text-zinc-400 transition-colors">
            <ChevronDown className={`w-3 h-3 transition-transform ${showOptions ? 'rotate-180' : ''}`} /> Options
          </button>
          {showOptions && (
            <div className="grid grid-cols-2 gap-2 mt-2 anim-fade-in" data-testid="advanced-options-panel">
              {[{ key: 'goal', ph: 'Goal' }, { key: 'geography', ph: 'Geography' }, { key: 'audience', ph: 'Audience' }, { key: 'tone', ph: 'Brand tone' }].map(({ key, ph }) => (
                <input key={key} data-testid={`option-${key}`} placeholder={ph} value={options[key]}
                  onChange={e => setOptions(p => ({ ...p, [key]: e.target.value }))}
                  className="bg-[#0F0F14] text-sm h-9 px-3 rounded-xl text-white outline-none placeholder:text-zinc-600 transition-all focus:ring-1 focus:ring-[#7C5CFF]/30"
                  style={{ border: '1px solid rgba(255,255,255,0.06)' }} />
              ))}
            </div>
          )}
        </div>

        {/* ═══ LIVE THINKING STRIP ═══ */}
        <div className="max-w-2xl mx-auto mt-6 anim-fade-up stagger-4" data-testid="live-thinking-strip">
          <div className="rounded-xl px-4 py-2.5 flex items-center gap-3" style={{ background: 'rgba(15,15,20,0.6)', border: '1px solid rgba(255,255,255,0.04)' }}>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <div className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
              <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-600">Live</span>
            </div>
            <div className="flex-1 overflow-hidden h-5 relative">
              <div key={signalIdx} className="absolute inset-0 flex items-center gap-2 anim-fade-in" style={{ animation: 'fadeIn 0.4s ease-out' }}>
                <span className="text-[12px] text-zinc-400 truncate">{signal.text}</span>
                <span className="text-[11px] font-mono font-bold flex-shrink-0" style={{ color: signal.color }}>{signal.delta}</span>
              </div>
            </div>
            <span className="text-[9px] font-mono text-zinc-700 flex-shrink-0">{liveCount.strategies > 0 ? `${liveCount.strategies.toLocaleString()} analyzed` : 'loading...'}</span>
          </div>
        </div>
      </div>

      {/* ═══ LIVE INTELLIGENCE PREVIEW (CLICKABLE) ═══ */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 pb-10 pt-4 anim-fade-up stagger-5">
        <div onClick={() => latest ? setPreviewModal(true) : null}
          className={`rounded-2xl p-6 relative overflow-hidden anim-float-3 transition-all duration-300 ${latest ? 'cursor-pointer' : ''}`}
          data-testid="preview-card"
          style={{ background: '#0F0F14', border: '1px solid rgba(255,255,255,0.06)', boxShadow: '0 4px 40px rgba(0,0,0,0.3), 0 0 60px rgba(124,92,255,0.04)' }}
          onMouseEnter={e => { if (latest) { e.currentTarget.style.transform = 'scale(1.01) translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 50px rgba(0,0,0,0.4), 0 0 80px rgba(124,92,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(124,92,255,0.2)'; }}}
          onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 40px rgba(0,0,0,0.3), 0 0 60px rgba(124,92,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}>

          <div className="absolute top-0 left-0 right-0 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(124,92,255,0.3), rgba(255,138,61,0.2), transparent)' }} />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#7C5CFF]">
                  {latest ? `Live: ${la.project_overview?.project_name || 'Analysis'}` : 'Live Intelligence'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {latest && <span className="text-[9px] font-mono text-zinc-600 flex items-center gap-1"><Eye className="w-3 h-3" />Click to explore</span>}
                <span className="text-[9px] font-mono text-zinc-700">powered by ai</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-5">
              {Object.entries(previewModes).map(([mode, score], i) => {
                const isBest = mode === previewBest; const color = MODE_COLORS[mode] || '#7C5CFF';
                return (
                  <div key={mode} className={`flex items-center gap-2 px-3.5 py-2 rounded-xl transition-all anim-float-${(i % 4) + 1}`}
                    style={{ background: isBest ? `${color}0D` : 'rgba(255,255,255,0.02)', border: `1px solid ${isBest ? `${color}25` : 'rgba(255,255,255,0.04)'}` }}>
                    <span className="text-xs font-medium capitalize" style={{ color: isBest ? color : '#52525B' }}>{mode}</span>
                    <span className="text-sm font-mono font-bold" style={{ color: isBest ? color : '#3F3F46' }}>{score}</span>
                    {isBest && <span className="text-[7px] font-bold uppercase px-1.5 py-0.5 rounded-full text-white anim-pulse" style={{ background: color }}>best</span>}
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {previewBlocks.map(({ label, value }, i) => (
                <div key={label} className={`rounded-xl px-3.5 py-3 transition-all anim-float-${(i % 4) + 1}`}
                  style={{ background: '#15151D', border: '1px solid rgba(255,255,255,0.04)' }}>
                  <span className="text-[9px] font-mono uppercase tracking-wider block mb-1" style={{ color: '#52525B' }}>{label}</span>
                  <p className="text-sm font-semibold text-zinc-200 truncate">{safeStr(value)}</p>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center anim-glow" style={{ background: 'rgba(124,92,255,0.1)', border: '1px solid rgba(124,92,255,0.15)' }}>
                <span className="font-mono text-sm font-bold text-[#7C5CFF]">{previewScore}</span>
              </div>
              <span className="text-xs text-zinc-500">Confidence — <span className="text-zinc-200 font-medium">{safeStr(previewInsight)}</span></span>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ TODAY'S EDGE ═══ */}
      <div className="relative z-10 max-w-3xl mx-auto px-4 pb-14">
        <div className="rounded-2xl px-6 py-5 relative overflow-hidden" data-testid="todays-edge"
          style={{ background: '#0F0F14', border: '1px solid rgba(124,92,255,0.1)', boxShadow: '0 0 40px rgba(124,92,255,0.06)' }}>
          <div className="absolute top-0 left-0 right-0 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(124,92,255,0.4), transparent)' }} />
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-[#22C55E] animate-pulse" />
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#7C5CFF]">Today's Edge</p>
          </div>
          <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
            <div className="flex-1">
              <p className="text-sm text-zinc-300 leading-relaxed mb-3">
                {topEdge ? (
                  <><span className="text-[#FF8A3D] font-bold">{topEdge.keyword}</span> {topEdge.headline?.toLowerCase().includes(topEdge.keyword?.toLowerCase()) ? topEdge.headline.replace(new RegExp(topEdge.keyword, 'i'), '').trim() : topEdge.headline} {topEdge.stat && <span className="text-[#22C55E] font-bold text-base">{topEdge.stat}</span>}</>
                ) : (
                  <><span className="text-[#FF8A3D] font-bold">Authority content</span> is outperforming viral content by <span className="text-[#22C55E] font-bold text-base">+32%</span> in fintech.</>
                )}
              </p>
              <div className="flex items-center gap-1.5">
                <ArrowRight className="w-3 h-3 text-[#7C5CFF]" />
                <span className="text-xs text-[#7C5CFF] font-medium">{topEdge?.recommendation || 'Use authority-first positioning today'}</span>
              </div>
            </div>
            <button onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }} data-testid="apply-edge-btn"
              className="flex-shrink-0 px-4 py-2 rounded-xl text-[11px] font-semibold text-white btn-glow flex items-center gap-1.5"
              style={{ background: 'linear-gradient(135deg, #7C5CFF, #FF8A3D)', boxShadow: '0 2px 12px rgba(124,92,255,0.25)' }}>
              <Zap className="w-3 h-3" />Apply to my product
            </button>
          </div>
        </div>
      </div>

      {/* ═══ 3-STEP FLOW ═══ */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 pb-16">
        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-0">
          {[
            { icon: Globe, label: 'Paste your product', color: '#7C5CFF' },
            { icon: Sparkles, label: 'AI finds your edge', color: '#3B82F6' },
            { icon: Rocket, label: 'Deploy instantly', color: '#FF8A3D' },
          ].map(({ icon: Icon, label, color }, i) => (
            <div key={i} className="flex-1 flex items-center">
              <div className="flex flex-col items-center flex-1">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 card-hover"
                  style={{ background: `${color}10`, border: `1px solid ${color}20`, boxShadow: `0 0 20px ${color}15` }}>
                  <Icon className="w-5 h-5" style={{ color }} />
                </div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-center">{label}</span>
              </div>
              {i < 2 && <div className="w-16 h-[1px] -mt-6 flex-shrink-0 hidden sm:block" style={{ background: `linear-gradient(90deg, ${color}30, rgba(255,255,255,0.04))` }} />}
            </div>
          ))}
        </div>
      </div>

      {/* ═══ STRATEGY SNAPSHOTS ═══ */}
      {projects.length > 0 && (
        <div className="relative z-10 px-4 pb-16" data-testid="recent-projects">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#7C5CFF] mb-2">Intelligence Archive</p>
              <h2 className="text-lg font-bold text-white">Recent Strategies</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((project, i) => {
                const name = project.analysis?.project_overview?.project_name || 'Untitled';
                const rawSc = project.analysis?.best_marketing_opportunity_today?.confidence_score;
                const pScore = rawSc > 0 && rawSc <= 10 ? rawSc * 10 : rawSc;
                const pHeadline = project.analysis?.best_marketing_opportunity_today?.title || '';
                const cat = project.analysis?.project_overview?.category || '';
                return (
                  <div key={project.id} data-testid={`project-card-${i}`} onClick={() => navigate(`/dashboard/${project.id}`)}
                    className="rounded-2xl p-5 cursor-pointer card-hover relative group"
                    style={{ background: '#15151D', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <button data-testid={`delete-project-${i}`} onClick={(e) => handleDelete(e, project.id)}
                      className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all p-1.5 rounded-lg hover:bg-[rgba(239,68,68,0.1)]" style={{ color: '#EF4444' }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-base font-bold text-white truncate">{name}</h3>
                      {pScore && (
                        <span className="font-mono text-sm font-bold flex-shrink-0 px-2.5 py-1 rounded-lg ml-2"
                          style={{ color: pScore >= 70 ? '#22C55E' : pScore >= 40 ? '#F59E0B' : '#EF4444', background: `${pScore >= 70 ? 'rgba(34,197,94,0.08)' : pScore >= 40 ? 'rgba(245,158,11,0.08)' : 'rgba(239,68,68,0.08)'}`, border: `1px solid ${pScore >= 70 ? 'rgba(34,197,94,0.15)' : pScore >= 40 ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)'}` }}>
                          {pScore}
                        </span>
                      )}
                    </div>
                    {cat && <span className="text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-md inline-block mb-2" style={{ color: '#7C5CFF', background: 'rgba(124,92,255,0.06)', border: '1px solid rgba(124,92,255,0.1)' }}>{cat}</span>}
                    {pHeadline && <p className="text-xs leading-relaxed text-zinc-500 line-clamp-2">{pHeadline}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ═══ LIVE METRICS (replaces trust badges) ═══ */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 pb-14" data-testid="live-metrics">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10 py-5 rounded-xl" style={{ background: 'rgba(15,15,20,0.5)', border: '1px solid rgba(255,255,255,0.04)' }}>
          <div className="text-center">
            <p className="text-2xl font-bold font-mono text-white" data-testid="strategies-count">{liveCount.strategies > 0 ? liveCount.strategies.toLocaleString() : '—'}</p>
            <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-0.5">products analyzed</p>
          </div>
          <div className="h-10 w-px hidden sm:block" style={{ background: 'rgba(255,255,255,0.06)' }} />
          <div className="text-center">
            <p className="text-2xl font-bold font-mono text-[#22C55E]" data-testid="deployed-count">{liveCount.deployed > 0 ? liveCount.deployed.toLocaleString() : '—'}</p>
            <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-0.5">strategies deployed</p>
          </div>
          <div className="h-10 w-px hidden sm:block" style={{ background: 'rgba(255,255,255,0.06)' }} />
          <div className="text-center">
            <p className="text-2xl font-bold font-mono text-[#FF8A3D]">{topEdge?.stat || '+32%'}</p>
            <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-0.5">{topEdge?.stat_label || 'avg. engagement lift'}</p>
          </div>
        </div>
      </div>

      {/* ═══ BOTTOM CTA ═══ */}
      <div className="relative z-10 max-w-3xl mx-auto px-4 pb-20 text-center">
        <h2 className="text-2xl font-bold text-white mb-2">Your growth edge is waiting.</h2>
        <p className="text-sm text-zinc-500 mb-6">10 seconds. One strategy. Ready to deploy.</p>
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} data-testid="bottom-cta"
          className="h-14 px-12 rounded-2xl text-white text-sm font-semibold btn-glow inline-flex items-center gap-2"
          style={{ background: 'linear-gradient(135deg, #7C5CFF, #FF8A3D)', boxShadow: '0 8px 30px rgba(124,92,255,0.3), 0 4px 20px rgba(255,138,61,0.2)' }}>
          Get Today's Move <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Footer */}
      <div className="relative z-10 text-center py-8 px-4 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <p className="text-[11px] text-zinc-600 font-mono">All rights reserved by NextMove, registered by G.F.T. Investments Private Limited 2026</p>
        <a href="https://gftworld.in" target="_blank" rel="noopener noreferrer" className="text-[10px] font-mono text-zinc-500 hover:text-[#7C5CFF] transition-colors">gftworld.in</a>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/gallery')} data-testid="gallery-link" className="text-[11px] font-mono text-[#7C5CFF] hover:text-white transition-colors">Gallery</button>
          <button onClick={() => navigate('/developers')} data-testid="api-link" className="text-[11px] font-mono text-zinc-400 hover:text-white transition-colors">API</button>
        </div>
      </div>

      {/* ═══ STRATEGY PREVIEW MODAL ═══ */}
      {previewModal && latest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="preview-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setPreviewModal(false)} />
          <div className="relative w-full max-w-2xl mx-4 rounded-2xl overflow-hidden max-h-[80vh] overflow-y-auto"
            style={{ background: '#0E0E14', border: '1px solid rgba(124,92,255,0.15)', boxShadow: '0 0 80px rgba(124,92,255,0.1)' }}>
            <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, #7C5CFF, #FF8A3D, #7C5CFF)', opacity: 0.7 }} />

            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <div>
                <h2 className="text-base font-bold text-white">{la.project_overview?.project_name || 'Strategy Breakdown'}</h2>
                <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">{la.project_overview?.category || ''}</p>
              </div>
              <button onClick={() => setPreviewModal(false)} data-testid="close-preview-modal" className="text-zinc-500 hover:text-white p-1.5 rounded-lg hover:bg-[#1F1F28] transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Mode scores */}
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">Strategy Modes</p>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(previewModes).map(([mode, score]) => {
                    const isBest = mode === previewBest; const color = MODE_COLORS[mode] || '#7C5CFF';
                    return (
                      <div key={mode} className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                        style={{ background: isBest ? `${color}0D` : '#15151D', border: `1px solid ${isBest ? `${color}25` : 'rgba(255,255,255,0.04)'}` }}>
                        <span className="text-xs capitalize" style={{ color: isBest ? color : '#52525B' }}>{mode}</span>
                        <span className="text-sm font-mono font-bold" style={{ color: isBest ? color : '#3F3F46' }}>{score}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Best move */}
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Best Move</p>
                <p className="text-base font-bold text-white">{la.best_marketing_opportunity_today?.title || 'N/A'}</p>
                {previewSummary && <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{previewSummary}</p>}
              </div>

              {/* Strategy blocks */}
              <div className="grid grid-cols-2 gap-3">
                {previewBlocks.map(({ label, value }) => (
                  <div key={label} className="rounded-xl px-3.5 py-2.5" style={{ background: '#15151D', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <span className="text-[9px] font-mono uppercase tracking-wider block mb-0.5 text-zinc-600">{label}</span>
                    <p className="text-sm font-medium text-zinc-200">{safeStr(value)}</p>
                  </div>
                ))}
              </div>

              {/* Confidence */}
              <div className="flex items-center gap-3 pt-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(124,92,255,0.1)', border: '1px solid rgba(124,92,255,0.15)' }}>
                  <span className="font-mono text-sm font-bold text-[#7C5CFF]">{previewScore}</span>
                </div>
                <span className="text-xs text-zinc-400">Confidence Score</span>
                <div className="flex-1" />
                <button onClick={() => { setPreviewModal(false); navigate(`/dashboard/${latest.id}`); }} data-testid="open-dashboard-btn"
                  className="px-5 py-2 rounded-xl text-sm font-semibold text-white btn-glow flex items-center gap-1.5"
                  style={{ background: 'linear-gradient(135deg, #7C5CFF, #FF8A3D)', boxShadow: '0 2px 12px rgba(124,92,255,0.25)' }}>
                  Open Dashboard <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
